"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  ArrowLeft,
  Bell,
  Bookmark,
  Building2,
  CalendarDays,
  Home,
  LayoutGrid,
  Menu,
  MessageCircle,
  Newspaper,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";

import { BrandLogo } from "@/components/brand/brand-logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useChatUnreadCount } from "@/hooks/use-chat";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

type MobileNavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
};

const PRIMARY_ITEMS: MobileNavItem[] = [
  { label: "Kezdőlap", href: "/", icon: Home },
  { label: "Chat", href: "/chat", icon: MessageCircle },
  { label: "Szolgáltatások", href: "/services", icon: LayoutGrid },
];

const MORE_ITEMS: MobileNavItem[] = [
  { label: "Beállítások", href: "/settings", icon: Settings },
];

const TOP_ITEMS: MobileNavItem[] = [
  { label: "Hírek", href: "/", icon: Newspaper },
  { label: "Közösségek", href: "/communities", icon: Users },
  { label: "Események", href: "/events", icon: CalendarDays },
  { label: "Mentett", href: "/saved", icon: Bookmark },
];

const MARKED_TITLE_ROOTS = new Set([
  "/chat",
  "/services",
  "/practices",
  "/profile",
  "/settings",
]);

const ROUTE_META = [
  { root: "/communities", title: "Közösségek" },
  { root: "/services", title: "Szolgáltatások" },
  { root: "/notifications", title: "Értesítések" },
  { root: "/practices", title: "Praxisaim" },
  { root: "/settings", title: "Beállítások" },
  { root: "/events", title: "Események" },
  { root: "/saved", title: "Mentések" },
  { root: "/profile", title: "Profilom" },
  { root: "/activity", title: "Aktivitás" },
  { root: "/feed", title: "Hírfolyam" },
  { root: "/chat", title: "Chat" },
] as const;

function isActive(pathname: string, href: string) {
  if (href === "/") {
    return pathname === "/";
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function routeMeta(pathname: string) {
  const section = ROUTE_META.find(
    ({ root }) => pathname === root || pathname.startsWith(`${root}/`),
  );
  if (!section) {
    return null;
  }
  return {
    ...section,
    detail: pathname !== section.root,
  };
}

function useMobileHomeChromeVisibility(pathname: string) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (pathname !== "/") {
      setVisible(true);
      return;
    }

    let previousY = Math.max(window.scrollY, 0);
    let accumulated = 0;
    let direction = 0;
    let frame: number | null = null;
    let ignoreUntil = 0;

    const update = () => {
      const currentY = Math.max(window.scrollY, 0);
      const delta = currentY - previousY;
      const nextDirection = Math.sign(delta);

      if (performance.now() < ignoreUntil) {
        previousY = currentY;
        frame = null;
        return;
      }

      if (currentY < 24) {
        accumulated = 0;
        setVisible(true);
      } else if (nextDirection !== 0) {
        if (nextDirection !== direction) {
          accumulated = 0;
          direction = nextDirection;
        }
        accumulated += delta;

        if (accumulated > 16) {
          setVisible(false);
          accumulated = 0;
          ignoreUntil = performance.now() + 260;
        } else if (accumulated < -12) {
          setVisible(true);
          accumulated = 0;
          ignoreUntil = performance.now() + 260;
        }
      }

      previousY = currentY;
      frame = null;
    };

    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [pathname]);

  return pathname === "/" ? visible : true;
}

export function MobileTopBar() {
  const pathname = usePathname();
  const router = useRouter();
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const meta = routeMeta(pathname);
  const showTopNavigation = TOP_ITEMS.some(({ href }) => pathname === href);
  const showMarkedTitle =
    Boolean(meta) &&
    !meta?.detail &&
    MARKED_TITLE_ROOTS.has(meta?.root ?? "");
  const showServiceDetailMark = meta?.root === "/services" && meta.detail;
  const homeChromeVisible = useMobileHomeChromeVisibility(pathname);

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b border-black/[0.05] bg-card/95 pt-[env(safe-area-inset-top)] backdrop-blur transition-shadow duration-200 md:hidden",
        pathname === "/" &&
          !homeChromeVisible &&
          "shadow-[0_8px_22px_rgba(15,23,42,0.2)]",
      )}
    >
      <div className="flex h-14 items-center gap-2 px-3">
        {meta?.detail && meta.root !== "/services" ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-11 shrink-0"
              aria-label="Vissza"
              onClick={() => router.push(meta.root)}
            >
              <ArrowLeft className="size-5" />
            </Button>
            <p className="min-w-0 flex-1 truncate text-base font-semibold">
              {meta.title}
            </p>
          </>
        ) : showServiceDetailMark ? (
          <BrandLogo
            className="min-w-0 flex-1"
            compact
            showWordmark={false}
          />
        ) : showTopNavigation ? (
          <BrandLogo className="min-w-0 flex-1" compact />
        ) : showMarkedTitle ? (
          <>
            <BrandLogo
              className="shrink-0"
              compact
              showWordmark={false}
            />
            <p className="min-w-0 flex-1 truncate text-lg font-semibold">
              {meta?.title}
            </p>
          </>
        ) : (
          <p className="min-w-0 flex-1 truncate px-1 text-lg font-semibold">
            {meta?.title ?? "PraxisTárs"}
          </p>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-2 rounded-full border border-black/[0.05] bg-card px-1.5 py-0.5 shadow-[0_4px_12px_rgba(15,23,42,0.12)]">
          <Link
            href="/practices"
            className={cn(
              "flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
              isActive(pathname, "/practices") && "bg-primary/10 text-primary",
            )}
            aria-label="Praxisaim"
            aria-current={isActive(pathname, "/practices") ? "page" : undefined}
          >
            <Building2 className="size-4" />
          </Link>
          <Link
            href="/notifications"
            className={cn(
              "flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
              pathname === "/notifications" && "bg-primary/10 text-primary",
            )}
            aria-label="Értesítések"
            aria-current={pathname === "/notifications" ? "page" : undefined}
          >
            <Bell className="size-4" />
          </Link>
          <Link
            href="/profile"
            className={cn(
              "flex size-7 items-center justify-center rounded-full outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
              isActive(pathname, "/profile") && "ring-2 ring-primary/30",
            )}
            aria-label="Profilom"
            aria-current={isActive(pathname, "/profile") ? "page" : undefined}
          >
            <UserAvatar
              name={user?.fullName ?? "Felhasználó"}
              path={user?.avatarPath}
              className="size-6 border border-black/[0.06]"
            />
          </Link>
        </div>
      </div>
      {showTopNavigation ? (
        <nav
          className={cn(
            "grid grid-cols-4 overflow-hidden transition-[height,opacity] duration-200 ease-out",
            homeChromeVisible
              ? "h-12 opacity-100"
              : "pointer-events-none h-0 opacity-0",
          )}
          aria-label="Kezdőlap szekciói"
          aria-hidden={!homeChromeVisible}
        >
          {TOP_ITEMS.map((item) => {
            const active = pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "relative flex min-h-11 items-center justify-center text-muted-foreground transition-colors active:bg-muted",
                  active && "text-primary",
                )}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
              >
                <Icon
                  className={cn("size-6", active && "fill-primary/10")}
                  strokeWidth={active ? 2.5 : 2}
                />
                {active ? (
                  <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary" />
                ) : null}
              </Link>
            );
          })}
        </nav>
      ) : null}
    </header>
  );
}

export function MobileBottomNavigation() {
  const pathname = usePathname();
  const [moreOpen, setMoreOpen] = useState(false);
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const userId = user?.accountStatus === "active" ? user.id : null;
  const unreadQuery = useChatUnreadCount(userId, Boolean(userId));
  const chatUnread = unreadQuery.unreadCount;
  const moreActive = MORE_ITEMS.some(({ href }) => isActive(pathname, href));
  const homeChromeVisible = useMobileHomeChromeVisibility(pathname);

  return (
    <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
      <nav
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-black/[0.06] bg-card/95 backdrop-blur transition-transform duration-200 ease-out md:hidden",
          homeChromeVisible
            ? "translate-y-0"
            : "pointer-events-none translate-y-full",
        )}
        aria-label="Fő navigáció"
        aria-hidden={!homeChromeVisible}
      >
        <div className="grid h-16 grid-cols-4 px-1">
          {PRIMARY_ITEMS.map((item) => {
            const active = isActive(pathname, item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "relative flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[10px] font-medium transition-colors",
                  active
                    ? "text-primary"
                    : "text-muted-foreground active:bg-muted",
                )}
                aria-current={active ? "page" : undefined}
              >
                <span className="relative">
                  <Icon
                    className={cn("size-5", active && "fill-primary/10")}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  {item.href === "/chat" && chatUnread > 0 ? (
                    <span className="absolute -right-3 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">
                      {chatUnread > 99 ? "99+" : chatUnread}
                    </span>
                  ) : null}
                </span>
                <span className="max-w-full truncate">{item.label}</span>
              </Link>
            );
          })}

          <Button
            type="button"
            variant="ghost"
            className={cn(
              "h-auto min-h-11 flex-col gap-0.5 rounded-xl px-1 text-[10px] font-medium",
              moreActive
                ? "text-primary hover:text-primary"
                : "text-muted-foreground",
            )}
            aria-label="További menüpontok"
            onClick={() => setMoreOpen(true)}
          >
            <Menu className="size-5" strokeWidth={moreActive ? 2.5 : 2} />
            <span>Továbbiak</span>
          </Button>
        </div>
      </nav>
      <MobileMoreMenu
        pathname={pathname}
        onNavigate={() => setMoreOpen(false)}
      />
    </Dialog>
  );
}

function MobileMoreMenu({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate: () => void;
}) {
  return (
    <DialogContent
      className="top-auto bottom-0 left-0 w-full max-w-none translate-x-0 translate-y-0 gap-0 rounded-t-3xl rounded-b-none border-x-0 border-b-0 p-0 pb-[env(safe-area-inset-bottom)] md:hidden"
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      <DialogHeader className="border-b px-5 py-4">
        <DialogTitle>Továbbiak</DialogTitle>
        <DialogDescription>
          A PraxisTárs további funkciói és beállításai.
        </DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-2 p-4">
        {MORE_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              className={cn(
                "flex min-h-14 items-center gap-3 rounded-2xl border bg-card px-3 py-3 text-sm font-medium transition-colors active:bg-muted",
                isActive(pathname, item.href)
                  ? "border-primary/30 bg-primary/5 text-primary"
                  : "text-foreground",
              )}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <Icon className="size-4.5" />
              </span>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </DialogContent>
  );
}
